import type { CSSProperties, ReactNode } from 'react';
import { sentinel } from '@sentinel/ui';

/**
 * How the user proves themselves for this step-up. Only one of `password` /
 * `totp` exists at a time — which one is decided by the ACR the backend asked
 * for — so the picker shows at most two tabs.
 */
export type StepUpMethod = 'password' | 'totp' | 'webauthn';

export interface StepUpMethodOption {
  id: StepUpMethod;
  label: string;
}

export interface StepUpMethodPickerProps {
  /** Accessible name for the tablist. */
  groupLabel: string;
  options: ReadonlyArray<StepUpMethodOption>;
  active: StepUpMethod;
  onChange: (method: StepUpMethod) => void;
}

// Authored as inline styles (matching the sibling federated-provider.tsx) so
// auth-core needs no direct StyleX dependency; the colours come from the
// sentinel design tokens.
const styles: Record<string, CSSProperties> = {
  bar: {
    display: 'flex',
    gap: '1.25rem',
    marginBottom: '0.25rem',
    borderBottom: `1px solid ${sentinel.surfaceSunken}`,
  },
  tab: {
    padding: '0.4375rem 0.125rem',
    border: 0,
    // Longhands, not the `borderBottom` shorthand: `tabActive` overrides only
    // the colour, and React refuses to mix a shorthand with a longhand — on
    // de-select it clears border-bottom-color and leaves the underline at
    // currentColor, so the previous tab keeps a visible rule.
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    background: 'transparent',
    font: 'inherit',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: sentinel.textMuted,
    cursor: 'pointer',
  },
  tabActive: {
    borderBottomColor: sentinel.accentAction,
    color: sentinel.textBody,
  },
};

/**
 * Tab bar for choosing a step-up method. Deliberately the same affordance and
 * the same ARIA shape as the login MFA challenge picker, so the user meets one
 * pattern in both places.
 */
export function StepUpMethodPicker({
  groupLabel,
  options,
  active,
  onChange,
}: StepUpMethodPickerProps) {
  return (
    <div role="tablist" aria-label={groupLabel} data-testid="step-up-method-tablist" style={styles.bar}>
      {options.map((option) => {
        const isActive = option.id === active;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            id={`step-up-method-${option.id}`}
            aria-selected={isActive}
            aria-controls={`step-up-panel-${option.id}`}
            data-testid={`step-up-method-${option.id}`}
            onClick={() => onChange(option.id)}
            style={isActive ? { ...styles.tab, ...styles.tabActive } : styles.tab}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface StepUpPanelProps {
  /** True when a picker is on screen; a lone panel needs no tabpanel role. */
  tabbed: boolean;
  method: StepUpMethod;
  children: ReactNode;
}

/**
 * Ties a method's content to its tab, and carries no tab semantics when there
 * is no picker.
 *
 * The element is always a `div`, even untabbed. Availability settles after the
 * dialog is already open, so `tabbed` flips from false to true under a mounted
 * form; swapping a Fragment for a `div` at that moment would remount the
 * subtree and throw away focus while the user is typing their password.
 */
export function StepUpPanel({ tabbed, method, children }: StepUpPanelProps) {
  return (
    <div
      {...(tabbed
        ? {
            role: 'tabpanel',
            id: `step-up-panel-${method}`,
            'aria-labelledby': `step-up-method-${method}`,
          }
        : {})}
      style={{ display: 'grid', gap: '0.6rem' }}
    >
      {children}
    </div>
  );
}
